import api from './api';

/**
 * Uploads a File to backend which forwards to Supabase Storage.
 * @param {string} bucket - one of 'prescriptions','lab-reports','provider-documents','profile-images'
 * @param {File} file
 * @returns {Promise<string>} url of uploaded file
 */
export async function uploadFile(bucket, file, options = {}) {
  if (!file) throw new Error('No file');

  const MAX_CLIENT_FILE_BYTES = 10 * 1024 * 1024;
  const MAX_IMAGE_INPUT_BYTES = 25 * 1024 * 1024;
  const MAX_PRESCRIPTION_IMAGE_BYTES = 1.5 * 1024 * 1024;
  const preserveFile = Boolean(options.preserveFile);

  let uploadFile = file;

  if (!preserveFile && file.type?.startsWith('image/')) {
    if (file.size > MAX_IMAGE_INPUT_BYTES) {
      throw new Error('Photo is too large. Please choose a photo under 25MB.');
    }
    uploadFile = await compressImageForUpload(file, MAX_PRESCRIPTION_IMAGE_BYTES);
  } else if (!preserveFile && file.size > MAX_CLIENT_FILE_BYTES) {
    throw new Error('File too large (max 10MB)');
  }

  if (!preserveFile && uploadFile.size > MAX_CLIENT_FILE_BYTES) {
    throw new Error('File is still too large after compression. Please choose a clearer, smaller photo.');
  }

  const form = new FormData();
  form.append('bucket', bucket);
  form.append('file', uploadFile, uploadFile.name || file.name || 'upload');

  let lastError;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const { data } = await api.post('/uploads/file', form, {
        timeout: 120000,
        maxContentLength: preserveFile ? Infinity : MAX_CLIENT_FILE_BYTES,
        maxBodyLength: preserveFile ? Infinity : MAX_CLIENT_FILE_BYTES,
        onUploadProgress: (event) => {
          if (typeof options.onProgress !== 'function') return;
          const total = event.total || uploadFile.size || 0;
          const progress = total > 0 ? Math.min(99, Math.round((event.loaded / total) * 100)) : 0;
          options.onProgress(progress);
        },
      });
      return data.url;
    } catch (error) {
      lastError = error;
      const retryable = !error.response || ['ECONNABORTED', 'ETIMEDOUT', 'ERR_NETWORK'].includes(error.code);
      if (!retryable || attempt === 1) throw error;
      await new Promise((resolve) => setTimeout(resolve, 700));
    }
  }
  throw lastError;
}

async function compressImageForUpload(file, targetBytes = 1.5 * 1024 * 1024) {
  if (file.size <= targetBytes) return file;

  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await loadImage(objectUrl);
    let maxDimension = 1800;
    let quality = 0.78;

    for (let attempt = 0; attempt < 5; attempt += 1) {
      const scale = Math.min(1, maxDimension / Math.max(image.naturalWidth || image.width, image.naturalHeight || image.height));
      const width = Math.max(1, Math.round((image.naturalWidth || image.width) * scale));
      const height = Math.max(1, Math.round((image.naturalHeight || image.height) * scale));
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d', { alpha: false });
      if (!ctx) return file;

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(image, 0, 0, width, height);

      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
      if (!blob) return file;
      if (blob.size <= targetBytes) {
        return new File([blob], replaceExtension(file.name || 'prescription.jpg', 'jpg'), {
          type: 'image/jpeg',
          lastModified: Date.now(),
        });
      }
      quality = Math.max(0.5, quality - 0.07);
      maxDimension = Math.max(1200, Math.round(maxDimension * 0.88));
    }

    throw new Error('Could not prepare this photo for upload. Please take a clearer photo with less background.');
  } catch {
    // HEIC and a few device-specific formats may not be decodable by the
    // browser. Keep the original and let the backend validate/store it.
    return file;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

function replaceExtension(filename, extension) {
  return filename.replace(/\.[^.]+$/, '') + '.' + extension;
}

/**
 * Reverse geocode coordinates to a human-readable address using OpenStreetMap Nominatim.
 * Returns an object {label, suburb, city, state, country} or null on failure.
 */
export async function reverseGeocode(lat, lng) {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
      { headers: { 'Accept-Language': 'en' } }
    );
    if (!res.ok) return null;
    const data = await res.json();
    const a = data.address || {};
    const suburb = a.suburb || a.neighbourhood || a.village || a.hamlet || a.locality || '';
    const city = a.city || a.town || a.county || a.state_district || '';
    return {
      label: data.display_name || '',
      lat: Number(lat),
      lng: Number(lng),
      suburb,
      city,
      state: a.state || '',
      country: a.country || '',
      short: [suburb, city].filter(Boolean).join(', '),
    };
  } catch {
    return null;
  }
}

/**
 * Search addresses by query using OpenStreetMap Nominatim.
 * Returns an array of {label, lat, lng, city, suburb}.
 */
export async function searchAddresses(query, country = 'in') {
  if (!query || query.trim().length < 2) return [];
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?format=jsonv2&q=${encodeURIComponent(query)}&countrycodes=${country}&limit=8&addressdetails=1`,
      { headers: { 'Accept-Language': 'en' } }
    );
    if (!res.ok) return [];
    const data = await res.json();
    return data.map((d) => {
      const a = d.address || {};
      const suburb = a.suburb || a.neighbourhood || a.village || a.hamlet || a.locality || '';
      const city = a.city || a.town || a.county || a.state_district || '';
      return {
        label: d.display_name,
        short: [suburb, city].filter(Boolean).join(', ') || d.display_name.split(',').slice(0, 2).join(', '),
        lat: parseFloat(d.lat),
        lng: parseFloat(d.lon),
        city,
        suburb,
        state: a.state || '',
      };
    });
  } catch {
    return [];
  }
}
